export interface VisitorGeo {
  countryCode: string
  countryName: string
  timezone: string
  cities: string[]
}

/**
 * Major cities per country, used by the social-proof notifications so the
 * visitor always sees cities from their own country.
 */
export const CITIES_BY_COUNTRY: Record<string, string[]> = {
  AR: [
    'Buenos Aires',
    'Córdoba',
    'Rosario',
    'Mendoza',
    'La Plata',
    'Mar del Plata',
    'San Miguel de Tucumán',
    'Salta',
    'Santa Fe',
    'Neuquén',
  ],
  MX: [
    'Ciudad de México',
    'Guadalajara',
    'Monterrey',
    'Puebla',
    'Tijuana',
    'León',
    'Querétaro',
    'Mérida',
    'Cancún',
    'Toluca',
  ],
  US: [
    'Miami',
    'Nueva York',
    'Los Ángeles',
    'Houston',
    'Chicago',
    'Dallas',
    'Phoenix',
    'San Antonio',
    'Orlando',
    'Atlanta',
  ],
  ES: [
    'Madrid',
    'Barcelona',
    'Valencia',
    'Sevilla',
    'Zaragoza',
    'Málaga',
    'Bilbao',
    'Murcia',
    'Palma de Mallorca',
    'Valladolid',
  ],
  CO: [
    'Bogotá',
    'Medellín',
    'Cali',
    'Barranquilla',
    'Cartagena',
    'Bucaramanga',
    'Pereira',
    'Cúcuta',
    'Santa Marta',
    'Manizales',
  ],
  CL: [
    'Santiago',
    'Valparaíso',
    'Concepción',
    'Viña del Mar',
    'Antofagasta',
    'La Serena',
    'Temuco',
    'Rancagua',
    'Puerto Montt',
    'Iquique',
  ],
  PE: [
    'Lima',
    'Arequipa',
    'Trujillo',
    'Chiclayo',
    'Piura',
    'Cusco',
    'Huancayo',
    'Iquitos',
    'Tacna',
    'Chimbote',
  ],
  EC: [
    'Guayaquil',
    'Quito',
    'Cuenca',
    'Santo Domingo',
    'Machala',
    'Manta',
    'Ambato',
    'Loja',
    'Riobamba',
    'Ibarra',
  ],
  UY: [
    'Montevideo',
    'Salto',
    'Ciudad de la Costa',
    'Paysandú',
    'Maldonado',
    'Rivera',
    'Las Piedras',
    'Tacuarembó',
    'Punta del Este',
    'Melo',
  ],
  PY: [
    'Asunción',
    'Ciudad del Este',
    'San Lorenzo',
    'Luque',
    'Capiatá',
    'Lambaré',
    'Fernando de la Mora',
    'Encarnación',
    'Pedro Juan Caballero',
    'Coronel Oviedo',
  ],
  BO: [
    'Santa Cruz de la Sierra',
    'La Paz',
    'Cochabamba',
    'El Alto',
    'Sucre',
    'Oruro',
    'Tarija',
    'Potosí',
    'Trinidad',
    'Montero',
  ],
  VE: [
    'Caracas',
    'Maracaibo',
    'Valencia',
    'Barquisimeto',
    'Maracay',
    'Ciudad Guayana',
    'Maturín',
    'San Cristóbal',
    'Cumaná',
    'Mérida',
  ],
  CR: [
    'San José',
    'Alajuela',
    'Cartago',
    'Heredia',
    'Liberia',
    'Puntarenas',
    'Limón',
    'Desamparados',
    'San Ramón',
    'Pérez Zeledón',
  ],
  PA: [
    'Ciudad de Panamá',
    'San Miguelito',
    'Colón',
    'David',
    'La Chorrera',
    'Arraiján',
    'Santiago',
    'Chitré',
    'Penonomé',
    'Aguadulce',
  ],
  DO: [
    'Santo Domingo',
    'Santiago de los Caballeros',
    'La Romana',
    'San Pedro de Macorís',
    'Punta Cana',
    'San Cristóbal',
    'Puerto Plata',
    'La Vega',
    'Higüey',
    'Moca',
  ],
  GT: [
    'Ciudad de Guatemala',
    'Quetzaltenango',
    'Villa Nueva',
    'Mixco',
    'Escuintla',
    'Cobán',
    'Huehuetenango',
    'Chimaltenango',
    'Antigua Guatemala',
    'Retalhuleu',
  ],
  SV: [
    'San Salvador',
    'Santa Ana',
    'San Miguel',
    'Soyapango',
    'Santa Tecla',
    'Apopa',
    'Mejicanos',
    'Ahuachapán',
    'Sonsonate',
    'Usulután',
  ],
  HN: [
    'Tegucigalpa',
    'San Pedro Sula',
    'Choloma',
    'La Ceiba',
    'El Progreso',
    'Comayagua',
    'Puerto Cortés',
    'Danlí',
    'Choluteca',
    'Juticalpa',
  ],
  NI: [
    'Managua',
    'León',
    'Masaya',
    'Granada',
    'Chinandega',
    'Matagalpa',
    'Estelí',
    'Jinotega',
    'Bluefields',
    'Rivas',
  ],
  PR: [
    'San Juan',
    'Bayamón',
    'Carolina',
    'Ponce',
    'Caguas',
    'Mayagüez',
    'Arecibo',
    'Guaynabo',
    'Aguadilla',
    'Humacao',
  ],
  CU: [
    'La Habana',
    'Santiago de Cuba',
    'Camagüey',
    'Holguín',
    'Santa Clara',
    'Guantánamo',
    'Bayamo',
    'Cienfuegos',
    'Matanzas',
    'Pinar del Río',
  ],
  BR: [
    'São Paulo',
    'Río de Janeiro',
    'Brasilia',
    'Salvador',
    'Fortaleza',
    'Belo Horizonte',
    'Curitiba',
    'Porto Alegre',
    'Recife',
    'Manaus',
  ],
  CA: [
    'Toronto',
    'Montreal',
    'Vancouver',
    'Calgary',
    'Ottawa',
    'Edmonton',
    'Winnipeg',
    'Quebec',
    'Hamilton',
    'Mississauga',
  ],
}

/** Used when the country cannot be resolved. */
export const DEFAULT_CITIES = [
  'Madrid',
  'Ciudad de México',
  'Buenos Aires',
  'Bogotá',
  'Santiago',
  'Lima',
  'Miami',
  'Barcelona',
  'Guadalajara',
  'Montevideo',
]

const COUNTRY_NAMES: Record<string, string> = {
  AR: 'Argentina',
  MX: 'México',
  US: 'Estados Unidos',
  ES: 'España',
  CO: 'Colombia',
  CL: 'Chile',
  PE: 'Perú',
  EC: 'Ecuador',
  UY: 'Uruguay',
  PY: 'Paraguay',
  BO: 'Bolivia',
  VE: 'Venezuela',
  CR: 'Costa Rica',
  PA: 'Panamá',
  DO: 'República Dominicana',
  GT: 'Guatemala',
  SV: 'El Salvador',
  HN: 'Honduras',
  NI: 'Nicaragua',
  PR: 'Puerto Rico',
  CU: 'Cuba',
  BR: 'Brasil',
  CA: 'Canadá',
}

/**
 * Rough timezone -> country map used as an offline fallback when the IP
 * lookup is blocked or unavailable.
 */
const TIMEZONE_COUNTRY: Record<string, string> = {
  'America/Argentina': 'AR',
  'America/Buenos_Aires': 'AR',
  'America/Cordoba': 'AR',
  'America/Mexico_City': 'MX',
  'America/Monterrey': 'MX',
  'America/Tijuana': 'MX',
  'America/Cancun': 'MX',
  'America/Merida': 'MX',
  'America/Bogota': 'CO',
  'America/Santiago': 'CL',
  'America/Lima': 'PE',
  'America/Guayaquil': 'EC',
  'America/Montevideo': 'UY',
  'America/Asuncion': 'PY',
  'America/La_Paz': 'BO',
  'America/Caracas': 'VE',
  'America/Costa_Rica': 'CR',
  'America/Panama': 'PA',
  'America/Santo_Domingo': 'DO',
  'America/Guatemala': 'GT',
  'America/El_Salvador': 'SV',
  'America/Tegucigalpa': 'HN',
  'America/Managua': 'NI',
  'America/Puerto_Rico': 'PR',
  'America/Havana': 'CU',
  'America/Sao_Paulo': 'BR',
  'America/Toronto': 'CA',
  'America/Vancouver': 'CA',
  'Europe/Madrid': 'ES',
  'Atlantic/Canary': 'ES',
  'America/New_York': 'US',
  'America/Chicago': 'US',
  'America/Denver': 'US',
  'America/Los_Angeles': 'US',
  'America/Phoenix': 'US',
}

export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

function countryFromTimezone(timezone: string): string {
  if (TIMEZONE_COUNTRY[timezone]) return TIMEZONE_COUNTRY[timezone]
  const region = timezone.split('/').slice(0, 2).join('/')
  return TIMEZONE_COUNTRY[region] || ''
}

export function citiesForCountry(countryCode: string): string[] {
  return CITIES_BY_COUNTRY[countryCode] || DEFAULT_CITIES
}

export function buildGeo(countryCode: string, timezone: string): VisitorGeo {
  const code = (countryCode || '').toUpperCase()
  return {
    countryCode: code,
    countryName: COUNTRY_NAMES[code] || '',
    timezone: timezone || getBrowserTimezone(),
    cities: citiesForCountry(code),
  }
}

/** Best guess without any network call — used as the initial render value. */
export function getFallbackGeo(): VisitorGeo {
  const timezone = getBrowserTimezone()
  return buildGeo(countryFromTimezone(timezone), timezone)
}

/** Resolves the visitor's country from their IP address. */
export async function fetchVisitorGeo(): Promise<VisitorGeo> {
  try {
    const response = await fetch('https://ipwho.is/', {
      cache: 'no-store',
    })
    if (!response.ok) throw new Error('geo lookup failed')
    const data = await response.json()
    const code: string = data?.country_code || ''
    const timezone: string = data?.timezone?.id || getBrowserTimezone()
    if (data?.success !== false && code) {
      return buildGeo(code, timezone)
    }
  } catch {
    /* fall through to the offline guess */
  }
  return getFallbackGeo()
}

/**
 * Today's date in the visitor's own timezone, written in Spanish.
 * e.g. "Miércoles 9 de septiembre de 2026"
 */
export function formatVisitorDate(timezone?: string): string {
  const tz = timezone || getBrowserTimezone()
  let formatted = ''
  try {
    formatted = new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: tz,
    }).format(new Date())
  } catch {
    formatted = new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date())
  }
  // Spanish formatters return "miércoles, 9 de septiembre de 2026"
  const clean = formatted.replace(',', '')
  return clean.charAt(0).toUpperCase() + clean.slice(1)
}
